import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, CheckCircle2, AlertTriangle, Clock, Camera, Copy, Trash2, Edit3, Video, Mic, User, FileSignature, MoreHorizontal, Plus, MessageSquare, X, XCircle } from 'lucide-react';
import { CameraButton } from '@/components/media/CameraButton';
import { VideoButton } from '@/components/media/VideoButton';
import { VoiceButton } from '@/components/media/VoiceButton';
import { SignatureCapture } from '@/components/SignatureCapture';

interface ChecklistItem {
  id: string;
  type: 'temperature' | 'photo' | 'voice_note' | 'text' | 'video' | 'cleaning' | 'restaurant' | 'bar' | 'visual' | 'verification' | 'weight_grid' | 'checkbox';
  label: string;
  description?: string;
  required: boolean;
  min_value?: number;
  max_value?: number;
  unit?: string;
  category?: string;
  subcategory?: string;
  sauce_options?: string[];
  editable?: boolean;
  expected_weight?: number;
  tolerance_percentage?: number;
  grid_columns?: number;
  weight_entries?: number[];
  supports_photos?: boolean;
  requires_photos?: boolean;
  supports_comments?: boolean;
  requires_comments?: boolean;
  min_photos?: number;
  max_photos?: number;
}

// Equipment lists for different categories
const EQUIPMENT_LISTS = {
  hot_holding: [
    "Pepper sauce",
    "Birria sauce",
    "Taco sauce",
    "Bolognese",
    "Fish soup",
    "BBQ sauce",
    "Béarnaise sauce",
    "Sweet & sour sauce"
  ],
  cold_holding: [
    "Mango salsa",
    "Mango sauce",
    "Chipotle aioli",
    "Hamburger dressing",
    "Salsa",
    "Guacamole",
    "Remoulade",
    "Pea purée",
    "Carbonara sauce",
    "Curry sauce"
  ],
  refrigeration_fridges: [
    'Walk-in Cooler', 'Prep Cooler', 'Meat Storage', 'Beverage Cooler', 
    'Dairy Fridge', 'Produce Cooler', 'Condiment Fridge', 'Staff Fridge'
  ],
  refrigeration_freezers: [
    'Main Freezer', 'Walk-in Freezer', 'Ice Cream Freezer', 'Meat Freezer',
    'Pastry Freezer', 'Backup Freezer', 'Ice Machine Area'
  ],
  kitchen_cleaning: [
    'Sanitizer Station', 'Equipment Area', 'Floor Area', 'Prep Surface',
    'Cooking Equipment', 'Storage Area', 'Handwash Station', 'Dish Area'
  ],
  restaurant_cleaning: [
    'Dining Area', 'Reception Area', 'Customer Restroom', 'Entrance',
    'Seating Area', 'Window Area', 'Floor Space', 'Customer Counter'
  ],
  bar_cleaning: [
    'Bar Counter', 'Drink Station', 'Glass Area', 'Bottle Storage',
    'Ice Station', 'Bar Equipment', 'Bar Floor', 'Customer Bar Area'
  ]
};

interface ChecklistEntry {
  item_id: string;
  status: 'ok' | 'warning' | 'critical';
  value?: string;
  note?: string;
  photo_url?: string;
  video_url?: string;
  voice_note_url?: string;
  corrective_action?: string;
}

interface Checklist {
  id: string;
  status: 'pending' | 'in_progress' | 'completed' | 'overdue';
  template_id: string;
  unit_id: string;
  user_name?: string;
  signature_url?: string;
  signature_data?: string;
  checklist_templates: {
    id: string;
    name: string;
    items: any; // Json from database
  };
  units: {
    name: string;
    tenant_id: string;
  };
}

const ChecklistExecutionModern = () => {
  const { checklistId } = useParams<{ checklistId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [entries, setEntries] = useState<Record<string, ChecklistEntry>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeCategory, setActiveCategory] = useState<'hot' | 'cold' | 'refrigeration' | 'kitchen' | 'restaurant' | 'bar' | 'visual' | 'weight'>('hot');
  const [activeSubcategory, setActiveSubcategory] = useState<'fridges' | 'freezers'>('fridges');
  const [userName, setUserName] = useState('');
  const [signatureData, setSignatureData] = useState('');
  const [signatureUploading, setSignatureUploading] = useState(false);
  const [editingItems, setEditingItems] = useState<Record<string, boolean>>({});
  
  const [customLabels, setCustomLabels] = useState<Record<string, string>>({});
  const [hiddenItemIds, setHiddenItemIds] = useState<Record<string, boolean>>({});
  const [expandedComments, setExpandedComments] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchChecklist();
  }, [checklistId, user]);

  useEffect(() => {
    if (!checklistId) return;
    try {
      const labels = localStorage.getItem(`checklist_${checklistId}_custom_labels`);
      const hidden = localStorage.getItem(`checklist_${checklistId}_hidden_items`);
      setCustomLabels(labels ? JSON.parse(labels) : {});
      const hiddenArr = hidden ? (JSON.parse(hidden) as string[]) : [];
      const map: Record<string, boolean> = {};
      hiddenArr.forEach((id) => { map[id] = true; });
      setHiddenItemIds(map);
    } catch (e) {
      // ignore localStorage errors
    }
  }, [checklistId]);

  // Open on the first tab that actually has items, so checklists without
  // hot-holding items (e.g. opening/closing checks) don't start on an empty tab.
  useEffect(() => {
    if (!checklist) return;
    const items = Array.isArray(checklist.checklist_templates?.items)
      ? checklist.checklist_templates.items
      : [];
    const has = (pred: (i: any) => boolean) => items.some((i: any) => i && typeof i === 'object' && pred(i));
    if (has((i) => (i.type === 'temperature' && i.min_value && i.min_value >= 55) || i.category === 'hot_holding')) setActiveCategory('hot');
    else if (has((i) => (i.type === 'temperature' && i.max_value && i.max_value <= 10) || i.category === 'cold_holding')) setActiveCategory('cold');
    else if (has((i) => i.category === 'refrigeration')) setActiveCategory('refrigeration');
    else if (has((i) => i.type === 'cleaning' || i.category === 'kitchen_cleaning')) setActiveCategory('kitchen');
    else if (has((i) => i.type === 'restaurant' || i.category === 'restaurant_cleaning')) setActiveCategory('restaurant');
    else if (has((i) => i.type === 'bar' || i.category === 'bar_cleaning')) setActiveCategory('bar');
    else if (has((i) => i.type === 'weight_grid' || i.category === 'weight_verification')) setActiveCategory('weight');
    else setActiveCategory('visual');
  }, [checklist]);

  const fetchChecklist = async () => {
    if (!checklistId || !user) return;

    try {
      const { data, error } = await supabase
        .from('checklists')
        .select(`
          *,
          checklist_templates!inner(id, name, items),
          units!inner(name, tenant_id)
        `)
        .eq('id', checklistId)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Checklist not found');

      setChecklist(data);
      setUserName(data.user_name || '');
      setSignatureData(data.signature_data || '');

      const items = Array.isArray(data.checklist_templates.items) 
        ? data.checklist_templates.items 
        : [];
      
      const initialEntries: Record<string, ChecklistEntry> = {};
      items.forEach((item: any) => {
        if (item && typeof item === 'object' && item.id) {
          initialEntries[item.id] = {
            item_id: item.id,
            status: 'ok',
            value: '',
            note: ''
          };
        }
      });
      setEntries(initialEntries);

      const { data: existingEntries } = await supabase
        .from('checklist_entries')
        .select('*')
        .eq('checklist_id', checklistId);

      if (existingEntries) {
        const entriesMap = { ...initialEntries };
        existingEntries.forEach((entry) => {
          entriesMap[entry.item_id] = {
            item_id: entry.item_id,
            status: entry.status as 'ok' | 'warning' | 'critical',
            value: entry.value || '',
            note: entry.note || '',
            photo_url: entry.photo_url || '',
            video_url: entry.video_url || '',
            voice_note_url: entry.voice_note_url || '',
            corrective_action: entry.corrective_action || ''
          };
        });
        setEntries(entriesMap);
      }

      if (data.status === 'pending') {
        await supabase
          .from('checklists')
          .update({ status: 'in_progress', started_at: new Date().toISOString() })
          .eq('id', checklistId);
      }
    } catch (error: any) {
      toast({
        title: "Error loading checklist",
        description: error.message,
        variant: "destructive",
      });
      navigate('/checklists');
    } finally {
      setLoading(false);
    }
  };

  const updateEntry = useCallback(async (itemId: string, updates: Partial<ChecklistEntry>) => {
    const newEntry = { ...entries[itemId], ...updates };
    setEntries(prev => ({ ...prev, [itemId]: newEntry }));

    try {
      await supabase
        .from('checklist_entries')
        .upsert({
          checklist_id: checklistId,
          item_id: itemId,
          status: newEntry.status,
          value: newEntry.value,
          note: newEntry.note,
          photo_url: newEntry.photo_url,
          video_url: newEntry.video_url,
          voice_note_url: newEntry.voice_note_url,
          corrective_action: newEntry.corrective_action
        });
    } catch (error: any) {
      toast({
        title: "Error saving entry",
        description: error.message,
        variant: "destructive",
      });
    }
  }, [entries, checklistId, toast]);

  const uploadFile = async (file: File, bucketName: string): Promise<string> => {
    if (!checklist?.units?.tenant_id) throw new Error('No tenant found');
    
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random()}.${fileExt}`;
    // Include tenant_id as first folder for RLS policy compliance
    const filePath = `${checklist.units.tenant_id}/${checklistId}/${fileName}`;

    const { error } = await supabase.storage
      .from(bucketName)
      .upload(filePath, file);

    if (error) throw error;

    const { data } = supabase.storage
      .from(bucketName)
      .getPublicUrl(filePath);

    return data.publicUrl;
  };

  const handlePhotoCapture = async (itemId: string, file: File) => {
    try {
      const url = await uploadFile(file, 'checklist-photos');
      updateEntry(itemId, { photo_url: url });
      toast({
        title: "Photo uploaded",
        description: "Photo has been saved successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleVideoCapture = async (itemId: string, file: File) => {
    try {
      const url = await uploadFile(file, 'checklist-videos');
      updateEntry(itemId, { video_url: url });
      toast({
        title: "Video uploaded",
        description: "Video has been saved successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleVoiceCapture = async (itemId: string, file: File) => {
    try {
      const url = await uploadFile(file, 'checklist-voice-notes');
      updateEntry(itemId, { voice_note_url: url });
      toast({
        title: "Voice note uploaded",
        description: "Voice note has been saved successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getTemperatureStatus = (value: string, item: ChecklistItem): 'ok' | 'warning' | 'critical' => {
    const temp = parseFloat(value);
    if (isNaN(temp)) return 'ok';

    if (item.min_value !== undefined && temp < item.min_value) {
      return temp < (item.min_value - 5) ? 'critical' : 'warning';
    }
    if (item.max_value !== undefined && temp > item.max_value) {
      return temp > (item.max_value + 5) ? 'critical' : 'warning';
    }
    return 'ok';
  };

  // Enhanced signature handling
  const handleSignatureCapture = async (signature: string) => {
    if (!signature) {
      setSignatureData('');
      return;
    }
    
    setSignatureUploading(true);
    try {
      setSignatureData(signature);
    } catch (error) {
      console.error('Signature handling error:', error);
      toast({
        title: "Signature Error",
        description: "Failed to process signature. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSignatureUploading(false);
    }
  };

  // Immediate signature save function
  const handleSignatureSave = async (signature: string) => {
    if (!signature || !checklistId) return;

    if (!userName.trim()) {
      toast({
        title: "Name required",
        description: "Please enter your name before saving signature.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Convert base64 to blob
      const response = await fetch(signature);
      const blob = await response.blob();
      const file = new File([blob], `signature_${checklistId}_${Date.now()}.png`, { type: 'image/png' });
      
      // Upload to signatures bucket with tenant_id prefix for RLS
      const tenantId = checklist?.units?.tenant_id;
      if (!tenantId) throw new Error('No tenant found');
      const fileName = `${tenantId}/${checklistId}_${Date.now()}.png`;
      const { error: uploadError } = await supabase.storage
        .from('signatures')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data } = supabase.storage
        .from('signatures')
        .getPublicUrl(fileName);
      
      const signatureUrl = data.publicUrl;

      // Update checklist with signature data, completion status, and timestamp
      const { error: updateError } = await supabase
        .from('checklists')
        .update({ 
          signature_data: signature,
          signature_url: signatureUrl,
          status: 'completed',
          completed_at: new Date().toISOString(),
          user_name: userName
        })
        .eq('id', checklistId);

      if (updateError) throw updateError;

      // Update local state
      setSignatureData(signature);
      if (checklist) {
        setChecklist(prev => prev ? {
          ...prev,
          signature_data: signature,
          signature_url: signatureUrl,
          status: 'completed',
          completed_at: new Date().toISOString(),
          user_name: userName
        } : null);
      }

      toast({
        title: "Checklist completed successfully!",
        description: "Your signature has been saved and checklist submitted. Redirecting to print...",
        action: (
          <div className="flex gap-2">
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => navigate(`/checklists/${checklistId}/print`)}
            >
              Print Report
            </Button>
          </div>
        ),
        duration: 10000,
      });

      // Navigate to print after a short delay
      setTimeout(() => {
        navigate(`/checklists/${checklistId}/print`);
      }, 2000);

    } catch (error: any) {
      console.error('Signature save error:', error);
      toast({
        title: "Save failed",
        description: error.message || "Failed to save signature. Please try again.",
        variant: "destructive",
      });
      throw error; // Re-throw to let SignatureCapture handle the error state
    }
  };

  const completeChecklist = async () => {
    if (!userName.trim()) {
      toast({
        title: "Name required",
        description: "Please enter your name before completing the checklist.",
        variant: "destructive",
      });
      return;
    }

    if (!signatureData) {
      toast({
        title: "Signature required",
        description: "Please provide your signature before completing the checklist.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      let signatureUrl = '';
      
      // Upload signature to storage if we have signature data
      if (signatureData) {
        try {
          // Convert base64 to blob
          const response = await fetch(signatureData);
          const blob = await response.blob();
          const file = new File([blob], `signature_${checklistId}_${Date.now()}.png`, { type: 'image/png' });
          
          // Upload to signatures bucket with tenant_id prefix for RLS
          const tenantId = checklist?.units?.tenant_id;
          if (!tenantId) throw new Error('No tenant found');
          const fileName = `${tenantId}/${checklistId}_${Date.now()}.png`;
          const { error: uploadError } = await supabase.storage
            .from('signatures')
            .upload(fileName, file);

          if (uploadError) throw uploadError;

          // Get public URL
          const { data } = supabase.storage
            .from('signatures')
            .getPublicUrl(fileName);
          
          signatureUrl = data.publicUrl;
        } catch (uploadError) {
          console.error('Signature upload error:', uploadError);
          // Continue with base64 data as fallback
        }
      }

      const { error: updateError } = await supabase
        .from('checklists')
        .update({ 
          status: 'completed', 
          completed_at: new Date().toISOString(),
          user_name: userName,
          signature_data: signatureData, // Keep base64 as backup
          signature_url: signatureUrl || null
        })
        .eq('id', checklistId);

      if (updateError) throw updateError;

      toast({
        title: "Checklist completed successfully!",
        description: "Your checklist has been submitted. You can now print or view the report.",
        action: (
          <div className="flex gap-2">
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => navigate(`/checklists/${checklistId}/print`)}
            >
              Print Report
            </Button>
          </div>
        ),
        duration: 10000,
      });

      // Small delay to ensure the status update is processed
      setTimeout(() => {
        navigate('/checklists');
      }, 1500);
    } catch (error: any) {
      toast({
        title: "Error completing checklist",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  // Item management functions
  const duplicateItem = async (item: ChecklistItem) => {
    const newItem = {
      ...item,
      id: `${item.id}_duplicate_${Date.now()}`,
      label: `${item.label} (Copy)`
    };
    
    if (checklist?.checklist_templates?.items) {
      const updatedItems = [...checklist.checklist_templates.items, newItem];
      
      // Update local state
      setChecklist(prev => prev ? {
        ...prev,
        checklist_templates: {
          ...prev.checklist_templates,
          items: updatedItems
        }
      } : null);

      // Persist to database
      try {
        const { error } = await supabase
          .from('checklist_templates')
          .update({ items: updatedItems })
          .eq('id', checklist.template_id);

        if (error) throw error;
      } catch (error: any) {
        toast({
          title: "Error saving custom item",
          description: error.message,
          variant: "destructive",
        });
      }
    }
  };

  const deleteItem = async (itemId: string) => {
    if (checklist?.checklist_templates?.items) {
      const updatedItems = checklist.checklist_templates.items.filter((item: any) => item.id !== itemId);
      
      // Update local state
      setChecklist(prev => prev ? {
        ...prev,
        checklist_templates: {
          ...prev.checklist_templates,
          items: updatedItems
        }
      } : null);
      
      // Also remove the entry for this item
      setEntries(prev => {
        const { [itemId]: removed, ...rest } = prev;
        return rest;
      });

      // Persist to database
      try {
        const { error } = await supabase
          .from('checklist_templates')
          .update({ items: updatedItems })
          .eq('id', checklist.template_id);

        if (error) throw error;
      } catch (error: any) {
        toast({
          title: "Error deleting custom item",
          description: error.message,
          variant: "destructive",
        });
      }
    }
  };

  const editItemName = async (itemId: string, newName: string) => {
    if (checklist?.checklist_templates?.items) {
      const updatedItems = checklist.checklist_templates.items.map((item: any) => 
        item.id === itemId ? { ...item, label: newName } : item
      );
      
      // Update local state
      setChecklist(prev => prev ? {
        ...prev,
        checklist_templates: {
          ...prev.checklist_templates,
          items: updatedItems
        }
      } : null);

      // Persist to database
      try {
        const { error } = await supabase
          .from('checklist_templates')
          .update({ items: updatedItems })
          .eq('id', checklist.template_id);

        if (error) throw error;
      } catch (error: any) {
        toast({
          title: "Error updating item name",
          description: error.message,
          variant: "destructive",
        });
      }
    }
  };

  const addCustomItem = async (category: string, subcategory?: string) => {
    const newItem: ChecklistItem = {
      id: `custom_${Date.now()}`,
      type: category === 'hot_holding' || category === 'cold_holding' || category.includes('refrigeration') ? 'temperature' : 'cleaning',
      label: 'New Custom Item',
      description: 'Custom item added by user',
      required: false,
      category: category,
      subcategory: subcategory,
      unit: category.includes('temperature') || category.includes('refrigeration') || category === 'hot_holding' || category === 'cold_holding' ? '°C' : undefined,
      editable: true
    };

    if (checklist?.checklist_templates?.items) {
      const updatedItems = [...checklist.checklist_templates.items, newItem];
      
      // Update local state
      setChecklist(prev => prev ? {
        ...prev,
        checklist_templates: {
          ...prev.checklist_templates,
          items: updatedItems
        }
      } : null);

      // Persist to database
      try {
        const { error } = await supabase
          .from('checklist_templates')
          .update({ items: updatedItems })
          .eq('id', checklist.template_id);

        if (error) throw error;

        toast({
          title: "Custom item added",
          description: "New item has been added and saved.",
        });
      } catch (error: any) {
        toast({
          title: "Error saving custom item",
          description: error.message,
          variant: "destructive",
        });
      }
    }
  };

  const getEquipmentList = (item: ChecklistItem) => {
    // First priority: Check item category if set
    if (item.category === 'hot_holding') return EQUIPMENT_LISTS.hot_holding;
    if (item.category === 'cold_holding') return EQUIPMENT_LISTS.cold_holding;
    if (item.category === 'refrigeration' && item.subcategory === 'fridges') return EQUIPMENT_LISTS.refrigeration_fridges;
    if (item.category === 'refrigeration' && item.subcategory === 'freezers') return EQUIPMENT_LISTS.refrigeration_freezers;
    if (item.category === 'kitchen_cleaning') return EQUIPMENT_LISTS.kitchen_cleaning;
    if (item.category === 'restaurant_cleaning') return EQUIPMENT_LISTS.restaurant_cleaning;
    if (item.category === 'bar_cleaning') return EQUIPMENT_LISTS.bar_cleaning;
    
    // Second priority: Fall back to temperature thresholds for temperature items
    if (item.type === 'temperature') {
      // Hot holding: min_value >= 55
      if (item.min_value !== undefined && item.min_value >= 55) {
        return EQUIPMENT_LISTS.hot_holding;
      }
      // Cold holding: max_value <= 10
      if (item.max_value !== undefined && item.max_value <= 10) {
        return EQUIPMENT_LISTS.cold_holding;
      }
    }
    
    // Third priority: Use current activeCategory as fallback
    switch (activeCategory) {
      case 'hot': return EQUIPMENT_LISTS.hot_holding;
      case 'cold': return EQUIPMENT_LISTS.cold_holding;
      case 'refrigeration': 
        return activeSubcategory === 'fridges' 
          ? EQUIPMENT_LISTS.refrigeration_fridges 
          : EQUIPMENT_LISTS.refrigeration_freezers;
      case 'kitchen': return EQUIPMENT_LISTS.kitchen_cleaning;
      case 'restaurant': return EQUIPMENT_LISTS.restaurant_cleaning;
      case 'bar': return EQUIPMENT_LISTS.bar_cleaning;
      default: return [];
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-accent/20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading checklist...</p>
        </div>
      </div>
    );
  }

  if (!checklist) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-accent/20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Checklist not found</h2>
          <p className="text-muted-foreground mb-4">The requested checklist could not be loaded.</p>
          <Button onClick={() => navigate('/checklists')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Checklists
          </Button>
        </div>
      </div>
    );
  }

  const items = Array.isArray(checklist.checklist_templates.items) 
    ? checklist.checklist_templates.items 
    : [];

  const allItemsArray = items;

  const hotHolding = allItemsArray.filter((item: any) => 
    item && typeof item === 'object' && (
      (item.type === 'temperature' && item.min_value && item.min_value >= 55) ||
      item.category === 'hot_holding'
    )
  ) as ChecklistItem[];
  
  const coldHolding = allItemsArray.filter((item: any) => 
    item && typeof item === 'object' && (
      (item.type === 'temperature' && item.max_value && item.max_value <= 10) ||
      item.category === 'cold_holding'
    )
  ) as ChecklistItem[];

  const refrigeration = allItemsArray.filter((item: any) => 
    item && typeof item === 'object' && 
    item.category === 'refrigeration'
  ) as ChecklistItem[];

  const fridges = refrigeration.filter((item: any) => 
    item.subcategory === 'fridges'
  ) as ChecklistItem[];

  const freezers = refrigeration.filter((item: any) => 
    item.subcategory === 'freezers'
  ) as ChecklistItem[];

  const kitchenCleaning = allItemsArray.filter((item: any) => 
    item && typeof item === 'object' && (
      item.type === 'cleaning' || item.category === 'kitchen_cleaning'
    )
  ) as ChecklistItem[];

  const restaurantCleaning = allItemsArray.filter((item: any) => 
    item && typeof item === 'object' && (
      item.type === 'restaurant' || item.category === 'restaurant_cleaning'
    )
  ) as ChecklistItem[];

  const barCleaning = allItemsArray.filter((item: any) => 
    item && typeof item === 'object' && (
      item.type === 'bar' || item.category === 'bar_cleaning'
    )
  ) as ChecklistItem[];
  
  const visualInspections = allItemsArray.filter((item: any) => 
    item && typeof item === 'object' && (
      item.type === 'visual' || item.category === 'visual_inspection' ||
      // Catch-all: any item type not handled by another bucket (checkbox, text, etc.)
      // lands here so it always renders somewhere.
      !['temperature', 'cleaning', 'restaurant', 'bar', 'weight_grid'].includes(item.type)
    )
  ) as ChecklistItem[];
  
  const weightVerification = allItemsArray.filter((item: any) => 
    item && typeof item === 'object' && (
      item.type === 'weight_grid' || item.category === 'weight_verification'
    )
  ) as ChecklistItem[];
  
  const getCurrentItems = () => {
    switch (activeCategory) {
      case 'hot': return hotHolding;
      case 'cold': return coldHolding;
      case 'refrigeration': 
        return activeSubcategory === 'fridges' ? fridges : freezers;
      case 'kitchen': return kitchenCleaning;
      case 'restaurant': return restaurantCleaning;
      case 'bar': return barCleaning;
      case 'visual': return visualInspections;
      case 'weight': return weightVerification;
      default: return hotHolding;
    }
  };

  const currentItems = getCurrentItems();
  const allCombinedItems = [...hotHolding, ...coldHolding, ...refrigeration, ...kitchenCleaning, ...restaurantCleaning, ...barCleaning, ...visualInspections, ...weightVerification];
  const requiredItems = allCombinedItems.filter(item => item.required);
  const completedRequired = requiredItems.filter(item => 
    entries[item.id]?.value && entries[item.id]?.value.trim() !== ''
  );
  const progressPercentage = requiredItems.length > 0 
    ? (completedRequired.length / requiredItems.length) * 100 
    : 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-accent/20">
      {/* Header */}
      <header className="glass-nav sticky top-0 z-10">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/checklists')}
                className="text-crystal-white hover:bg-white/10"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <div>
                <h1 className="text-lg font-semibold text-crystal-white">
                  {checklist.checklist_templates.name}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {checklist.units.name}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="text-sm text-muted-foreground">
                {completedRequired.length}/{requiredItems.length} completed
              </div>
              <Progress value={progressPercentage} className="w-20" />
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-4 pb-32">
        {/* User Information */}
        <Card className="glass-panel mb-4">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-crystal-orange" />
              <Input
                placeholder="Enter your name"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                onBlur={async () => {
                  if (userName.trim() && checklistId) {
                    try {
                      await supabase
                        .from('checklists')
                        .update({ user_name: userName })
                        .eq('id', checklistId);
                    } catch (error) {
                      console.error('Error saving user name:', error);
                    }
                  }
                }}
                className="glass-input flex-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Category Tabs */}
        <div className="flex gap-2 mb-4 flex-wrap">
          <Button
            variant={activeCategory === 'hot' ? 'default' : 'outline'}
            onClick={() => setActiveCategory('hot')}
            className={activeCategory === 'hot' ? 'bg-red-500 hover:bg-red-600' : 'hover:bg-red-50'}
          >
            🔥 Hot Holding ({hotHolding.length})
          </Button>
          <Button
            variant={activeCategory === 'cold' ? 'default' : 'outline'}
            onClick={() => setActiveCategory('cold')}
            className={activeCategory === 'cold' ? 'bg-blue-500 hover:bg-blue-600' : 'hover:bg-blue-50'}
          >
            ❄️ Cold Holding ({coldHolding.length})
          </Button>
          <Button
            variant={activeCategory === 'refrigeration' ? 'default' : 'outline'}
            onClick={() => setActiveCategory('refrigeration')}
            className={activeCategory === 'refrigeration' ? 'bg-cyan-500 hover:bg-cyan-600' : 'hover:bg-cyan-50'}
          >
            🧊 Refrigeration ({refrigeration.length})
          </Button>
          <Button
            variant={activeCategory === 'kitchen' ? 'default' : 'outline'}
            onClick={() => setActiveCategory('kitchen')}
            className={activeCategory === 'kitchen' ? 'bg-green-500 hover:bg-green-600' : 'hover:bg-green-50'}
          >
            👨‍🍳 Kitchen Cleaning ({kitchenCleaning.length})
          </Button>
          <Button
            variant={activeCategory === 'restaurant' ? 'default' : 'outline'}
            onClick={() => setActiveCategory('restaurant')}
            className={activeCategory === 'restaurant' ? 'bg-purple-500 hover:bg-purple-600' : 'hover:bg-purple-50'}
          >
            🍽️ Restaurant Cleaning ({restaurantCleaning.length})
          </Button>
          <Button
            variant={activeCategory === 'bar' ? 'default' : 'outline'}
            onClick={() => setActiveCategory('bar')}
            className={activeCategory === 'bar' ? 'bg-orange-500 hover:bg-orange-600' : 'hover:bg-orange-50'}
          >
            🍸 Bar Cleaning ({barCleaning.length})
          </Button>
          <Button
            variant={activeCategory === 'visual' ? 'default' : 'outline'}
            onClick={() => setActiveCategory('visual')}
            className={activeCategory === 'visual' ? 'bg-indigo-500 hover:bg-indigo-600' : 'hover:bg-indigo-50'}
          >
            👁️ Visual Inspections ({visualInspections.length})
          </Button>
          <Button
            variant={activeCategory === 'weight' ? 'default' : 'outline'}
            onClick={() => setActiveCategory('weight')}
            className={activeCategory === 'weight' ? 'bg-yellow-500 hover:bg-yellow-600' : 'hover:bg-yellow-50'}
          >
            ⚖️ Weight Verification ({weightVerification.length})
          </Button>
        </div>

        {/* Refrigeration Subcategory Tabs */}
        {activeCategory === 'refrigeration' && (
          <div className="flex gap-2 mb-4 flex-wrap">
            <Button
              variant={activeSubcategory === 'fridges' ? 'default' : 'outline'}
              onClick={() => setActiveSubcategory('fridges')}
              className={activeSubcategory === 'fridges' ? 'bg-blue-400 hover:bg-blue-500' : 'hover:bg-blue-50'}
            >
              ❄️ Fridges ({fridges.length}) <span className="ml-1 text-xs">0-4°C / 32-39°F</span>
            </Button>
            <Button
              variant={activeSubcategory === 'freezers' ? 'default' : 'outline'}
              onClick={() => setActiveSubcategory('freezers')}
              className={activeSubcategory === 'freezers' ? 'bg-cyan-600 hover:bg-cyan-700' : 'hover:bg-cyan-50'}
            >
              🧊 Freezers ({freezers.length}) <span className="ml-1 text-xs">-21 to -18°C / -5.8 to -0.4°F</span>
            </Button>
          </div>
        )}

        {/* Add Custom Item Button */}
        <div className="flex justify-end mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const categoryMapping = {
                'hot': 'hot_holding',
                'cold': 'cold_holding', 
                'refrigeration': activeSubcategory === 'fridges' ? 'refrigeration' : 'refrigeration',
                'kitchen': 'kitchen_cleaning',
                'restaurant': 'restaurant_cleaning',
                'bar': 'bar_cleaning',
                'visual': 'visual_inspection'
              };
              const subcategoryMapping = {
                'refrigeration': activeSubcategory === 'fridges' ? 'fridges' : 'freezers'
              };
              addCustomItem(
                categoryMapping[activeCategory as keyof typeof categoryMapping], 
                activeCategory === 'refrigeration' ? subcategoryMapping[activeCategory] : undefined
              );
            }}
            className="bg-background/80 hover:bg-background/60 border-dashed"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Custom {activeCategory === 'refrigeration' ? 
              (activeSubcategory === 'fridges' ? 'Fridge' : 'Freezer') : 
              activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)} Item
          </Button>
        </div>

        {/* Items Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {currentItems.filter((item) => !hiddenItemIds[item.id]).map((item: ChecklistItem) => {
            const entry = entries[item.id];
            const status = item.type === 'temperature' && entry?.value 
              ? getTemperatureStatus(entry.value, item)
              : entry?.status || 'ok';

            // Build unique options: current label + equipment list + sauce options (case-insensitive, trimmed)
            const sourceOptions = [
              item.label,
              ...getEquipmentList(item),
              ...(item.sauce_options ?? []),
            ]
              .filter(Boolean)
              .map((s) => s.trim());

            const seen = new Set<string>();
            const uniqueOptions = sourceOptions.filter((opt) => {
              const key = opt.toLowerCase();
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });

            return (
              <Card key={item.id} className="glass-panel border-border/50 bg-card/50 backdrop-blur-sm">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 flex items-center gap-2">
                      {entry?.value && entry.value.trim() !== '' && (
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-500 flex-shrink-0">
                          <CheckCircle2 className="h-5 w-5 text-white" />
                        </div>
                      )}
                      <Select
                        value={customLabels[item.id] ?? item.label} 
                        onValueChange={(value) => {
                          if (value === 'custom_input') {
                            const customName = prompt('Enter custom equipment name:');
                            if (customName && customName.trim()) {
                              editItemName(item.id, customName.trim());
                              setCustomLabels(prev => {
                                const next = { ...prev, [item.id]: customName.trim() };
                                try {
                                  localStorage.setItem(`checklist_${checklistId}_custom_labels`, JSON.stringify(next));
                                } catch (err) {
                                  // ignore localStorage errors
                                }
                                return next;
                              });
                            }
                          } else {
                            editItemName(item.id, value);
                            setCustomLabels((prev) => {
                              const next = { ...prev, [item.id]: value };
                              try {
                                localStorage.setItem(`checklist_${checklistId}_custom_labels`, JSON.stringify(next));
                              } catch (err) {
                                // ignore localStorage errors
                              }
                              return next;
                            });
                          }
                        }}
                      >
                        <SelectTrigger className="w-full bg-background/80 border-border/50 text-left text-sm font-medium">
                          <SelectValue placeholder="Select equipment" />
                        </SelectTrigger>
                        <SelectContent className="bg-popover border-border z-50">
                          {uniqueOptions.map((opt) => (
                            <SelectItem key={opt.toLowerCase()} value={opt}>{opt}</SelectItem>
                          ))}
                          <SelectItem value="custom_input">
                            <div className="flex items-center gap-2">
                              <Plus className="h-4 w-4" />
                              Add Custom Equipment
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      
                      {item.type === 'temperature' && (item.min_value !== undefined || item.max_value !== undefined) && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          Target: {item.min_value !== undefined ? `${item.min_value}` : ''}
                          {item.min_value !== undefined && item.max_value !== undefined ? ' - ' : ''}
                          {item.max_value !== undefined ? `${item.max_value}` : ''}{item.unit}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      <Badge variant={
                        status === 'critical' ? 'destructive' :
                        status === 'warning' ? 'secondary' : 
                        'default'
                      } className={`text-xs px-2 py-1 ${
                        status === 'ok' ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''
                      }`}>
                        {status === 'critical' ? 'Critical' :
                         status === 'warning' ? 'Warning' : 'Normal'}
                      </Badge>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-popover border-border z-50">
                          <DropdownMenuItem 
                            onClick={() => duplicateItem(item)}
                            className="cursor-pointer"
                          >
                            <Copy className="h-4 w-4 mr-2" />
                            Duplicate Item
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => {
                              const newName = prompt('Enter new name:', customLabels[item.id] || item.label);
                              if (newName && newName.trim()) {
                                editItemName(item.id, newName.trim());
                                setCustomLabels(prev => {
                                  const next = { ...prev, [item.id]: newName.trim() };
                                  try {
                                    localStorage.setItem(`checklist_${checklistId}_custom_labels`, JSON.stringify(next));
                                  } catch (err) {
                                    // ignore localStorage errors
                                  }
                                  return next;
                                });
                              }
                            }}
                            className="cursor-pointer"
                          >
                            <Edit3 className="h-4 w-4 mr-2" />
                            Edit Name
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => {
                              if (confirm('Are you sure you want to delete this item?')) {
                                deleteItem(item.id);
                              }
                            }}
                            className="cursor-pointer text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Item
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4 pt-0">
                   {item.type === 'temperature' && (
                     <div className="space-y-3">
                       <div className="relative">
                         <Input
                           type="number"
                           placeholder="Enter temperature"
                           value={entry?.value || ''}
                           onChange={(e) => {
                             const newStatus = getTemperatureStatus(e.target.value, item);
                             updateEntry(item.id, { 
                               value: e.target.value,
                               status: newStatus
                             });
                           }}
                           className="pr-12 text-center text-lg font-medium bg-background/80"
                         />
                         <div className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">
                           {item.unit}
                         </div>
                       </div>
                       
                       <div className="grid grid-cols-3 gap-2">
                         <Button 
                           size="sm" 
                           variant={status === 'ok' ? 'default' : 'outline'}
                           onClick={() => updateEntry(item.id, { status: 'ok' })}
                           className={`${status === 'ok' ? 'bg-green-500 hover:bg-green-600 text-white' : 'border-green-300 text-green-700 hover:bg-green-50'}`}
                         >
                           <CheckCircle2 className="h-4 w-4 mr-1" />
                           Normal
                         </Button>
                         <Button 
                           size="sm" 
                           variant={status === 'warning' ? 'default' : 'outline'}
                           onClick={() => updateEntry(item.id, { status: 'warning' })}
                           className={`${status === 'warning' ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : 'border-yellow-300 text-yellow-700 hover:bg-yellow-50'}`}
                         >
                           <AlertTriangle className="h-4 w-4 mr-1" />
                           Warning
                         </Button>
                         <Button 
                           size="sm" 
                           variant={status === 'critical' ? 'default' : 'outline'}
                           onClick={() => updateEntry(item.id, { status: 'critical' })}
                           className={`${status === 'critical' ? 'bg-red-500 hover:bg-red-600 text-white' : 'border-red-300 text-red-700 hover:bg-red-50'}`}
                         >
                           <AlertTriangle className="h-4 w-4 mr-1" />
                           Critical
                         </Button>
                       </div>
                     </div>
                   )}

                    {(item.type === 'visual' || item.type === 'cleaning' || item.type === 'verification' || item.type === 'checkbox') && (
                      <div className="space-y-3">
                        {item.description && (
                          <p className="text-sm text-muted-foreground bg-muted/50 p-2 rounded">
                            {item.description}
                          </p>
                        )}
                        
                        <div className="grid grid-cols-2 gap-2">
                          <Button 
                            size="lg" 
                            variant={entry?.status === 'ok' ? 'default' : 'outline'}
                            onClick={() => updateEntry(item.id, { status: 'ok', value: 'ok' })}
                            className={`h-14 text-base font-semibold ${entry?.status === 'ok' ? 'bg-green-500 hover:bg-green-600 text-white shadow-lg' : 'border-2 border-green-300 text-green-700 hover:bg-green-50'}`}
                          >
                            <CheckCircle2 className="h-5 w-5 mr-2" />
                            ✓ OK
                          </Button>
                          <Button 
                            size="lg" 
                            variant={entry?.status === 'critical' ? 'default' : 'outline'}
                            onClick={() => updateEntry(item.id, { status: 'critical', value: 'avvik' })}
                            className={`h-14 text-base font-semibold ${entry?.status === 'critical' ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg' : 'border-2 border-red-300 text-red-700 hover:bg-red-50'}`}
                          >
                            <XCircle className="h-5 w-5 mr-2" />
                            ✗ AVVIK
                          </Button>
                        </div>
                        
                        {/* Photo capture if enabled */}
                        {item.supports_photos && (
                          <div className="flex gap-2 items-center flex-wrap">
                            <CameraButton
                              onPhotoCapture={async (file) => {
                                await handlePhotoCapture(item.id, file);
                              }}
                              disabled={false}
                            />
                            {item.requires_photos && !entry?.photo_url && (
                              <span className="text-xs text-destructive">Photo required</span>
                            )}
                            {entry?.photo_url && (
                              <Badge variant="secondary" className="text-xs">
                                <Camera className="h-3 w-3 mr-1" />
                                Photo captured
                              </Badge>
                            )}
                          </div>
                        )}
                        
                        {/* Comment box for visual checks */}
                        {item.supports_comments !== false && (
                          <div className="mt-2">
                            <Textarea
                              placeholder={item.requires_comments ? "Kommentar påkrevd / Comment required..." : "Kommentar / Comment..."}
                              value={entry?.note || ''}
                              onChange={(e) => updateEntry(item.id, { note: e.target.value })}
                              className={`min-h-[60px] text-sm bg-background/80 border-border/50 focus:border-primary resize-none ${item.requires_comments && !entry?.note ? 'border-destructive' : ''}`}
                              required={item.requires_comments}
                            />
                            {item.requires_comments && !entry?.note && (
                              <span className="text-xs text-destructive">Comment required</span>
                            )}
                          </div>
                        )}
                      </div>
                     )}

                    {item.type === 'weight_grid' && (
                      <div className="space-y-4">
                        {item.description && (
                          <p className="text-sm text-muted-foreground bg-muted/50 p-2 rounded">
                            {item.description}
                          </p>
                        )}
                        
                        <div className="grid grid-cols-6 gap-2">
                          {Array.from({ length: item.grid_columns || 6 }, (_, index) => {
                            const weightEntries = entry?.value ? JSON.parse(entry.value) : [];
                            const weightValue = weightEntries[index] || '';
                            const expectedWeight = item.expected_weight || 0;
                            const tolerance = item.tolerance_percentage || 10;
                            const isWithinTolerance = weightValue ? 
                              Math.abs(weightValue - expectedWeight) <= (expectedWeight * tolerance / 100) : true;
                            
                            return (
                              <div key={index} className="space-y-1">
                                <label className="text-xs font-medium text-muted-foreground">
                                  Weight {index + 1}
                                </label>
                                <Input
                                  type="number"
                                  placeholder="0.0"
                                  value={weightValue}
                                  onChange={(e) => {
                                    const newWeights = [...weightEntries];
                                    newWeights[index] = parseFloat(e.target.value) || 0;
                                    const allCompliant = newWeights.every(w => 
                                      w && Math.abs(w - expectedWeight) <= (expectedWeight * tolerance / 100)
                                    );
                                    updateEntry(item.id, { 
                                      value: JSON.stringify(newWeights),
                                      status: allCompliant ? 'ok' : 'critical'
                                    });
                                  }}
                                  className={`text-sm ${!isWithinTolerance && weightValue ? 'border-red-500 bg-red-50' : ''}`}
                                />
                                {weightValue && !isWithinTolerance && (
                                  <div className="text-xs text-red-600">Outside tolerance</div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        
                        {item.expected_weight && (
                          <div className="text-sm text-muted-foreground bg-muted/30 p-2 rounded">
                            Expected: {item.expected_weight}g (±{item.tolerance_percentage || 10}%)
                          </div>
                        )}
                        
                        <div className="grid grid-cols-3 gap-2">
                          <Button 
                            size="sm" 
                            variant={entry?.status === 'ok' ? 'default' : 'outline'}
                            onClick={() => updateEntry(item.id, { status: 'ok' })}
                            className={`${entry?.status === 'ok' ? 'bg-green-500 hover:bg-green-600 text-white' : 'border-green-300 text-green-700 hover:bg-green-50'}`}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Compliant
                          </Button>
                          <Button 
                            size="sm" 
                            variant={entry?.status === 'warning' ? 'default' : 'outline'}
                            onClick={() => updateEntry(item.id, { status: 'warning' })}
                            className={`${entry?.status === 'warning' ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : 'border-yellow-300 text-yellow-700 hover:bg-yellow-50'}`}
                          >
                            <AlertTriangle className="h-4 w-4 mr-1" />
                            Minor Deviation
                          </Button>
                          <Button 
                            size="sm" 
                            variant={entry?.status === 'critical' ? 'default' : 'outline'}
                            onClick={() => updateEntry(item.id, { status: 'critical' })}
                            className={`${entry?.status === 'critical' ? 'bg-red-500 hover:bg-red-600 text-white' : 'border-red-300 text-red-700 hover:bg-red-50'}`}
                          >
                            <AlertTriangle className="h-4 w-4 mr-1" />
                            Non-Compliant
                          </Button>
                        </div>
                      </div>
                    )}

                    {item.type === 'text' && (
                      <div className="space-y-3">
                        {item.description && (
                          <p className="text-sm text-muted-foreground bg-muted/50 p-2 rounded">
                            {item.description}
                          </p>
                        )}
                        <Textarea
                          placeholder="Enter details..."
                          value={entry?.value || ''}
                          onChange={(e) => updateEntry(item.id, { value: e.target.value, status: 'ok' })}
                          className="min-h-[80px] text-sm bg-background/80 border-border/50 focus:border-primary resize-none"
                        />
                      </div>
                    )}

                    {/* Enhanced Media Capture */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-2">
                      <CameraButton onPhotoCapture={(file) => handlePhotoCapture(item.id, file)} />
                      <VideoButton onVideoCapture={(file) => handleVideoCapture(item.id, file)} />
                      <VoiceButton onVoiceCapture={(file) => handleVoiceCapture(item.id, file)} />
                    </div>

                    {/* Media Gallery */}
                    {(entry?.photo_url || entry?.video_url || entry?.voice_note_url) && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-foreground">Captured Media</h4>
                        <div className="grid grid-cols-3 gap-2">
                          {entry.photo_url && (
                            <div className="relative aspect-square rounded-lg overflow-hidden bg-muted">
                              <img 
                                src={entry.photo_url} 
                                alt="Captured evidence" 
                                className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer"
                                onClick={() => window.open(entry.photo_url, '_blank')}
                              />
                            </div>
                          )}
                          {entry.video_url && (
                            <div className="relative aspect-square rounded-lg overflow-hidden bg-muted">
                              <video 
                                src={entry.video_url} 
                                className="w-full h-full object-cover cursor-pointer"
                                controls
                                preload="metadata"
                              />
                            </div>
                          )}
                          {entry.voice_note_url && (
                            <div className="relative aspect-square rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                              <audio 
                                src={entry.voice_note_url} 
                                controls
                                className="w-full"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Comment Section - Prominent and Collapsible */}
                  <div className="border-t border-border/50 pt-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpandedComments(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
                      className="w-full justify-start text-sm font-medium hover:bg-muted/50"
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      KOMMENTAR / COMMENT
                      {entry?.note && entry.note.trim() && (
                        <Badge variant="secondary" className="ml-2">Added</Badge>
                      )}
                    </Button>
                    
                    {(expandedComments[item.id] || entry?.note) && (
                      <div className="mt-2">
                        <Textarea
                          placeholder="Legg til kommentar her... / Add comment here..."
                          value={entry?.note || ''}
                          onChange={(e) => updateEntry(item.id, { note: e.target.value })}
                          className="min-h-[80px] bg-background border-2 border-border/50 focus:border-primary resize-none"
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Signature Section */}
        <Card className="glass-panel mb-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSignature className="h-5 w-5 text-crystal-orange" />
              Digital Signature
            </CardTitle>
          </CardHeader>
          <CardContent>
            <SignatureCapture
              onSignature={handleSignatureCapture}
              onSave={handleSignatureSave}
              signature={signatureData}
              loading={signatureUploading}
              onNameChange={setUserName}
            />
          </CardContent>
        </Card>
      </main>

      {/* Fixed Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border p-4">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="text-sm text-muted-foreground">
              Progress: {completedRequired.length}/{requiredItems.length}
            </div>
            <Progress value={progressPercentage} className="w-32" />
          </div>
          
          <Button 
            onClick={completeChecklist}
            disabled={saving || completedRequired.length < requiredItems.length}
            className="bg-gradient-to-r from-crystal-orange to-crystal-orange/80 hover:from-crystal-orange/90 hover:to-crystal-orange/70"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Completing...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Complete Checklist
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChecklistExecutionModern;